import Icon from '../../../components/AppIcon';
import { ContributionRecord } from '../types';

interface ContributionHistoryTableProps {
  contributions: ContributionRecord[];
  onViewDetails?: (id: string) => void;
}

const ContributionHistoryTable = ({ contributions, onViewDetails }: ContributionHistoryTableProps) => {
  const getStatusBadge = (status: ContributionRecord['verificationStatus']) => {
    const statusConfig = {
      verified: {
        label: 'Verified',
        color: 'var(--color-success)',
        bgColor: 'var(--color-success)',
        icon: 'CheckCircle',
      },
      pending: {
        label: 'Pending',
        color: 'var(--color-warning)',
        bgColor: 'var(--color-warning)',
        icon: 'Clock',
      },
      rejected: {
        label: 'Rejected',
        color: 'var(--color-error)',
        bgColor: 'var(--color-error)',
        icon: 'XCircle',
      },
    };

    const config = statusConfig[status];

    return (
      <span
        className="inline-flex items-center gap-1 sm:gap-1.5 px-2 sm:px-2.5 py-1 rounded-full text-xs font-bold shadow-sm"
        style={{
          backgroundColor: `${config.bgColor}15`,
          color: config.color
        }}
      >
        <Icon name={config.icon} size={12} color={config.color} className="sm:w-3.5 sm:h-3.5" />
        <span className="hidden sm:inline">{config.label}</span>
      </span>
    );
  };

  const formatDate = (date: Date) => {
    const parsedDate = date instanceof Date ? date : new Date(date);
    return parsedDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatAmount = (amount: number) => {
    return `${amount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} VT`;
  };

  return (
    <div className="overflow-hidden">
      {/* Desktop Table View */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-bold text-muted-foreground uppercase tracking-wider">
                Member
              </th>
              <th className="px-4 py-3 text-left text-xs font-bold text-muted-foreground uppercase tracking-wider">
                Date
              </th>
              <th className="px-4 py-3 text-left text-xs font-bold text-muted-foreground uppercase tracking-wider">
                Amount
              </th>
              <th className="px-4 py-3 text-left text-xs font-bold text-muted-foreground uppercase tracking-wider">
                Recorded By
              </th>
              <th className="px-4 py-3 text-left text-xs font-bold text-muted-foreground uppercase tracking-wider">
                Status
              </th>
              <th className="px-4 py-3 text-left text-xs font-bold text-muted-foreground uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {contributions.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center">
                  <Icon name="FileText" size={48} className="text-muted-foreground mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground">No contributions recorded yet</p>
                </td>
              </tr>
            ) : (
              contributions.map((contribution) => (
                <tr
                  key={contribution.id}
                  className={`
                    hover:bg-muted/30 transition-all duration-200
                    ${contribution.isCurrentUser ? 'bg-primary/10 border-l-4 border-l-primary' : ''}
                  `}
                >
                  <td className="px-4 py-3 text-sm text-foreground font-medium">
                    {contribution.memberName || 'Member'}
                    {contribution.isCurrentUser && <span className="ml-2 text-xs text-primary font-bold">(You)</span>}
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground">
                    {formatDate(contribution.date)}
                  </td>
                  <td className="px-4 py-3 text-sm font-bold text-foreground">
                    {formatAmount(contribution.amount)}
                  </td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">
                    {contribution.recordedBy}
                  </td>
                  <td className="px-4 py-3">
                    {getStatusBadge(contribution.verificationStatus)}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => onViewDetails?.(contribution.id)}
                      className="text-primary hover:text-primary/80 transition-all duration-200 font-semibold flex items-center gap-1 text-sm hover:scale-105"
                    >
                      <Icon name="Eye" size={16} />
                      <span>View</span>
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-3">
        {contributions.length === 0 ? (
          <div className="px-4 py-12 text-center">
            <Icon name="FileText" size={48} className="text-muted-foreground mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">No contributions recorded yet</p>
          </div>
        ) : (
          contributions.map((contribution) => (
            <div
              key={contribution.id}
              className={`
                p-4 rounded-lg border-2 transition-all duration-200 hover:shadow-md
                ${contribution.isCurrentUser ? 'bg-primary/5 border-primary/30' : 'bg-card border-border'}
              `}
            >
              <div className="flex justify-between items-start mb-3">
                <div className="flex-1">
                  <h4 className="text-sm font-bold text-foreground mb-1">
                    {contribution.memberName || 'Member'}
                    {contribution.isCurrentUser && (
                      <span className="ml-2 text-xs bg-primary text-primary-foreground px-2 py-0.5 rounded-full">You</span>
                    )}
                  </h4>
                  <p className="text-xs text-muted-foreground">{formatDate(contribution.date)}</p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-foreground">{formatAmount(contribution.amount)}</p>
                </div>
              </div>

              <div className="space-y-2 mb-3">
                <div className="flex justify-between items-center text-xs">
                  <span className="text-muted-foreground">Recorded by:</span>
                  <span className="text-foreground font-medium">{contribution.recordedBy}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-muted-foreground">Status:</span>
                  {getStatusBadge(contribution.verificationStatus)}
                </div>
              </div>

              <button
                onClick={() => onViewDetails?.(contribution.id)}
                className="w-full py-2 px-3 bg-primary/10 text-primary hover:bg-primary hover:text-primary-foreground rounded-lg transition-all duration-200 font-semibold text-sm flex items-center justify-center gap-2"
              >
                <Icon name="Eye" size={16} />
                <span>View Details</span>
              </button>
            </div>
          ))
        )}
      </div>

      {contributions.length > 0 && (
        <div className="mt-4 p-3 sm:p-4 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-2">
          <p className="text-xs sm:text-sm text-muted-foreground font-medium">
            Showing {contributions.length} contribution{contributions.length !== 1 ? 's' : ''}
          </p>
        </div>
      )}
    </div>
  );
};

export default ContributionHistoryTable;